import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { EMPTY, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap
} from 'rxjs/operators';
import { SystemMessageType } from '@aid/core/types/enums/system-message-type.enum';
import { OrganizationService } from '@aid/core/services/organization.service';
import { SystemMessage } from '@aid/core/types/classes';
import { ChatSystemMessage } from '@aid/chat/types/classes';
import {
  FirebaseService,
  FirebaseValueConstraint
} from '@aid/core/firebase/firebase.service';

@Injectable({
  providedIn: 'root'
})
export class SystemChannelService {
  private readonly firebase = inject(FirebaseService);
  private readonly organizationService = inject(OrganizationService);

  subscribe(
    type: SystemMessageType,
    resourceId?: number | null
  ): Observable<SystemMessage[]> {
    return this.organizationService.organization$.pipe(
      map(organization => organization?.slug ?? null),
      distinctUntilChanged(),
      switchMap(organization => {
        if (!organization) {
          return EMPTY;
        }

        const constraints: FirebaseValueConstraint[] = [
          {
            kind: 'where',
            field: 'creationDate',
            operation: '>=',
            value: moment().toDate()
          },
          {
            kind: 'where',
            field: 'organization',
            operation: '==',
            value: organization
          },
          { kind: 'where', field: 'type', operation: '==', value: type }
        ];

        if (resourceId) {
          constraints.push({
            kind: 'where',
            field: 'resourceId',
            operation: '==',
            value: resourceId
          });
        }

        return this.firebase.valueChanges<SystemMessage>('system', constraints);
      }),
      filter(value => value.length > 0),
      debounceTime(200)
    );
  }
  subscribeChat(
    receiverId: number,
    senderId: number
  ): Observable<ChatSystemMessage[]> {
    const currentDate = moment().toDate();
    return this.firebase
      .valueChanges<ChatSystemMessage>('system', [
        {
          kind: 'where',
          field: 'creationDate',
          operation: '>=',
          value: currentDate
        },
        {
          kind: 'where',
          field: 'type',
          operation: '==',
          value: SystemMessageType.CHAT
        },
        {
          kind: 'where',
          field: 'receiverId',
          operation: '==',
          value: receiverId
        },
        {
          kind: 'where',
          field: 'senderId',
          operation: '==',
          value: senderId
        }
      ])
      .pipe(
        filter(value => value.length > 0),
        debounceTime(200)
      );
  }
}
